import React, { useRef, useEffect, useState, useCallback } from "react";
import { loadGoogleMaps, parseAddressComponents } from "utils/googleMaps";

const AddressAutocomplete = ({ onSelect, placeholder, className, disabled }) => {
  const containerRef = useRef(null);
  const elementRef = useRef(null);
  const onSelectRef = useRef(onSelect);
  const [isGoogleLoaded, setIsGoogleLoaded] = useState(false);
  const [isLoadingKey, setIsLoadingKey] = useState(true);
  onSelectRef.current = onSelect;

  const handlePlaceSelection = useCallback(async (place) => {
    try {
      await place.fetchFields({
        fields: ["displayName", "formattedAddress", "location", "addressComponents", "id"],
      });
      const location = place.location;
      onSelectRef.current?.({
        placeId: place.id,
        formattedAddress: place.formattedAddress,
        latitude: typeof location?.lat === "function" ? location.lat() : null,
        longitude: typeof location?.lng === "function" ? location.lng() : null,
        name: place.displayName,
        addressComponents: parseAddressComponents(place.addressComponents || []),
      });
    } catch (err) {
      console.error("Failed to fetch place fields:", err);
    }
  }, []);

  useEffect(() => {
    let isMounted = true;

    const init = async () => {
      try {
        setIsLoadingKey(true);
        const google = await loadGoogleMaps();
        setIsLoadingKey(false);
        if (!isMounted || !containerRef.current || elementRef.current) return;
        if (!google.maps.places.PlaceAutocompleteElement) {
          throw new Error("PlaceAutocompleteElement not loaded");
        }

        const el = new google.maps.places.PlaceAutocompleteElement({});
        el.style.width = "100%";
        if (placeholder) el.setAttribute("placeholder", placeholder);

        const onSelectEvent = async (event) => {
          const pred = event.placePrediction || event.detail?.placePrediction;
          const directPlace = event.place || event.detail?.place;
          if (pred?.toPlace) {
            handlePlaceSelection(pred.toPlace());
            return;
          }
          if (directPlace) handlePlaceSelection(directPlace);
        };

        el.addEventListener("gmp-select", onSelectEvent);
        el.addEventListener("gmp-placeselect", onSelectEvent);
        el._onSelectEvent = onSelectEvent;

        containerRef.current.appendChild(el);
        elementRef.current = el;
        if (isMounted) setIsGoogleLoaded(true);
      } catch (err) {
        setIsLoadingKey(false);
        console.error("Failed to load Google Places element:", err);
      }
    };

    init();

    return () => {
      isMounted = false;
      if (elementRef.current) {
        if (elementRef.current._onSelectEvent) {
          elementRef.current.removeEventListener("gmp-select", elementRef.current._onSelectEvent);
          elementRef.current.removeEventListener("gmp-placeselect", elementRef.current._onSelectEvent);
        }
        if (elementRef.current.parentNode) {
          elementRef.current.parentNode.removeChild(elementRef.current);
        }
      }
      elementRef.current = null;
    };
  }, [placeholder, handlePlaceSelection]);

  return (
    <div className="relative w-full">
      <div ref={containerRef} className={className} />
      {(!isGoogleLoaded || isLoadingKey) && (
        <div className="absolute right-3 top-1/2 -translate-y-1/2">
          <div className="w-4 h-4 border-2 border-[#4F46E5] border-t-transparent rounded-full animate-spin"></div>
        </div>
      )}
      {disabled && <div className="absolute inset-0 bg-white/50 cursor-not-allowed" />}
    </div>
  );
};

export default AddressAutocomplete;
